/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/10/22
 */

import React from "react";
import {Button, Card, CardActions, CardContent, CardHeader, Chip, Stack} from "@mui/material";
import {Employee} from "../interfaces/Global";
import Avatar from "../components/Avatar";
import ModelView from "../components/ModelView";
import countryNames from "../const/country-names";
import EmployeeSettingsDialog, {convertStringTimeToDate} from "./EmployeeSettingsDialog";
import {format} from "../dates-helper";
import stateNames from "../const/state-names";

export interface EmployeeDetailsProps {
	employee: Employee
}

export default function EmployeeDetails({ employee }: EmployeeDetailsProps) {

	const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);

	return (
		<Card>
			<CardHeader
				avatar={<Avatar id={employee.id as string} />}
				title={employee.displayName}
				subheader={employee.jobTitle}
			/>
			<CardContent>
				<ModelView
					attributes={[
						"id",
						"firstName",
						"lastName",
						"location",
						"department",
						"division",
						{ attribute: "company", label: "Product" },
						{ attribute: "city", hidden: !employee.city },
						{ attribute: "state", hidden: !employee.state, value: employee.countryCode && employee.state ? stateNames[employee.countryCode]?.[employee.state] ?? employee.state : "" },
						{ label: "Country", value: countryNames[employee.countryCode ?? ""] },
						{ label: "Manager", value: employee.manager?.displayName + (employee.manager ? " (" + employee.manager.id + ")" : "")},
						{ label: "Date Of Hire", hidden: !employee.dateOfHire, value: employee.dateOfHire && format(employee.dateOfHire, 'PPP') },
						{ label: "Date Of Birth", hidden: !employee.dateOfBirth, value: employee.dateOfBirth && format(employee.dateOfBirth, 'LLLL do') },
						"timeZone",
						{ label: "Start Time", value: format(convertStringTimeToDate(employee.startTime), 'p') },
						{ label: "End Time", value: format(convertStringTimeToDate(employee.endTime), 'p') },
						{ attribute: "gsacOooEnabled", label: "GSAC OOO", format: "boolean" },
						{ label: "Capacity Modifier", value: Math.round(employee.capacityModifier*100) + "%" },
						{ label: "Custom Tags", value: employee.tags ? <Stack spacing={.5} direction={'row'} component={"span"}>{employee.tags.map(t => <Chip component={"span"} key={t} label={t} size={"small"} />)}</Stack> : null }
					]}
					model={employee}
					labelSx={{
						pr: 1,
						width: 130,
						textAlign: "right"
					}}
					valueSx={{
						maxWidth: 500,
						maxHeight: 200,
						overflow: "auto"
					}}
					nullTypographyProps={{
						variant: "body2"
					}}
					labelTypographyProps={{
						color: "textSecondary",
						variant: "body2"
					}}
					valueTypographyProps={{
						variant: "body2"
					}}
				/>
			</CardContent>
			<CardActions disableSpacing>
				<Button
					size="small"
					onClick={() => setSettingsDialogOpen(true)}
				>
					Settings
				</Button>
			</CardActions>
			<EmployeeSettingsDialog
				employee={employee}
				open={settingsDialogOpen}
				onClose={() => setSettingsDialogOpen(false)}
			/>
		</Card>
	);

}